import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  ComboBox,
  Group,
  Input,
  ListBox,
  ListBoxItem,
  ListBoxItemProps,
  Popover,
} from "react-aria-components";
import { DropdownProps, Option } from "types/DropDown";
import CancelIcon from "../../../assets/icons/cancel.svg";
import { useAppSelector } from "../../../redux/hooks";
import Const from "../../../const.json";
import "./DropDown.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import { Form } from "react-bootstrap";
import arrowDown from "../../../assets/icons/arrow-down.svg";

const DropDown: React.FC<DropdownProps> = ({
  options,
  title,
  placeholder,
  onSelect,
  selectedOption: initialSelectedOption,
}) => {
  const [selectedOption, setSelectedOption] = useState<Option | null>(
    initialSelectedOption
  );
  const [inputValue, setInputValue] = useState("");
  const [fetchedOptions, setFetchedOptions] = useState<Option[]>([]);
  const { period } = useAppSelector(state => state.general);

  useEffect(() => {
    setSelectedOption(initialSelectedOption);
    setInputValue('');
  }, [initialSelectedOption, period]);

  const fetchOptions = async () => {
    try {
      const result = await options();
      setFetchedOptions(result);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  const handleSelect = (selectedId: string | null) => {
    const selected = fetchedOptions.find((option) => option.id === selectedId);
    setSelectedOption(selected || null);
    if (selected && onSelect) {
      onSelect(selected.id);
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const clearSelection = () => {
    setSelectedOption(null);
    setInputValue("");
    if (onSelect) {
      onSelect(null);
    }
  };

  return (
    <div className="DropDown">
      <Form className="custom-dropdown-form" onSubmit={event => event.preventDefault()}>
        <Form.Group>
          <ComboBox
            className="custom-dropdown"
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onSelectionChange={handleSelect}
            aria-label={title}
          >
            <Group className="d-flex justify-content-between align-items-center">
              <Input
                className="d-flex"
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={placeholder}
              />
              {selectedOption && (
                <button
                  onClick={clearSelection}
                  aria-label={Const.clearSelection}
                >
                  <img src={CancelIcon} alt={Const.clear} />
                </button>
              )}
              <Button>
                <img src={arrowDown} alt={Const.downIcon} />
              </Button>
            </Group>
            <Popover className="custom-dropdown-menu">
              <ListBox className="outline-none p-1 dropdown-list">
                {fetchedOptions.map((option) => (
                  <UserItem
                    key={option.id}
                    id={option.id}
                    textValue={option.description}
                  >
                    <span>{option.description}</span>
                  </UserItem>
                ))}
              </ListBox>
            </Popover>
          </ComboBox>
        </Form.Group>
      </Form>
    </div>
  );
};

function UserItem(props: ListBoxItemProps & { children: React.ReactNode; id: string }) {
  return (
    <ListBoxItem {...props} id={props.id} className="dropdown-listitem" textValue={props.textValue}>
      {({ isSelected, isFocused }) => (
        <span className={`flex-1 flex items-center gap-3 truncate font-normal ${isSelected ? "group-selected:font-medium selected" : ""} ${isFocused ? "focused" : ""}`}>
          {props.children}
        </span>
      )}
    </ListBoxItem>
  );
}

export default DropDown;